import { Static, Type } from '@sinclair/typebox';
import { SurveyStates } from '../../../enums/SurveyStates';
import { makeNullable } from '../../../utils/typeboxHelpers';

export const surveyTableSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    surveryedOn: makeNullable(Type.String()),
    updatedOn: Type.String(),
    status: Type.Enum(SurveyStates),
    description: makeNullable(Type.String()),
    /** Text about survey results. Can be in MD format. */
    summaryAndAdvice: makeNullable(Type.String()),
    /** Determines user access rights. The operator is for example the drone company that does not do the survey. */
    operatorCompanyId: makeNullable(Type.String({ format: 'uuid' })),
    /** Determines user access rights. The surveyor company. */
    surveyorCompanyId: makeNullable(Type.String({ format: 'uuid' })),
    objectId: Type.String({ format: 'uuid' }),
    created_at: makeNullable(Type.String()),
    updated_at: makeNullable(Type.String()),
    /** The condition. Is a one digit number. */
    condition: makeNullable(Type.String({ maxLength: 1 })),
    /** Total storage for survey. These are documents, videos, photos and 3D models. */
    storageUsed: makeNullable(Type.Number()),
    /** DOM lighting in Potree, can be on or off in 3D viewer */
    isDOMLight: makeNullable(Type.Boolean()),
    /** Asset description box is always visible. Does not seem te in use. */
    isAssetDescriptionVisible: makeNullable(Type.Boolean()),
    /** Annotations in 3D viewer default visible */
    isAnnotationsDefaultVisible: makeNullable(Type.Boolean()),
    /** Uploaded videos visible in survey */
    isVideoDownloadVisible: makeNullable(Type.Boolean()),
    /** Automatically generated number */
    projectNumber: makeNullable(Type.String({ maxLength: 255 })),
    /** Determine which survey form is displayed */
    inspectionStandardType: Type.String({ maxLength: 255 }),
    /** Markdown description of the survey scope shown on web edit and iPhone-app. Free text. */
    definedScope: makeNullable(Type.String()),
    /** Free-form remarks captured in the iPhone-app's "Opmerkingen" tab. */
    surveyRemarks: makeNullable(Type.String()),
    /** The care condition. Is a one digit number. */
    careCondition: makeNullable(Type.String({ maxLength: 1 })),
    /** Lines with measurement data displayed in a 3D model */
    scanLineCoordinates: makeNullable(Type.Any()),
    /** Oiltank shape in 3D viewer */
    shapesInModel: makeNullable(Type.Any()),
    /** FMECA in survey form */
    isFmecaAvailable: Type.Boolean(),
    /** CRA in survey form. Is Amsterdam specific. */
    isCraAvailable: Type.Boolean(),
    craInitialHistoryIsBuildBetween4074: Type.Boolean(),
    craInitialHistoryIsStaticallyIndeterminate: Type.Boolean(),
    craInitialHistoryBuildBetween4074Remarks: makeNullable(Type.String()),
    craInitialHistoryStaticallyIndeterminateRemarks: makeNullable(Type.String()),
    craInspectionIsBuildBetween4074: Type.Boolean(),
    craInspectionIsStaticallyIndeterminate: Type.Boolean(),
    craInspectionBuildBetween4074Remarks: makeNullable(Type.String()),
    craInspectionStaticallyIndeterminateRemarks: makeNullable(Type.String()),
    craInitialHistoryRemarks: makeNullable(Type.String()),
    craInspectionRemarks: makeNullable(Type.String()),
    craInitialHistoryScore: makeNullable(Type.Number()),
    craInspectionScore: makeNullable(Type.Number()),
    craInitialHistoryCondition: Type.Integer(),
    craInspectionCondition: Type.Integer(),
    craInitialHistoryConditionWithoutFactor: Type.Integer(),
    craInspectionConditionWithoutFactor: Type.Integer(),
    /** Author who prepared or created the survey report */
    preparedAuthor: makeNullable(Type.String({ maxLength: 255 })),
    preparedDate: makeNullable(Type.String()),
    /** Author who verified the survey report */
    verifiedAuthor: makeNullable(Type.String({ maxLength: 255 })),
    verifiedDate: makeNullable(Type.String()),
    craInitialHistoryConditionScoreWithoutFactor: makeNullable(Type.Integer()),
    craInspectionConditionScoreWithoutFactor: makeNullable(Type.Integer()),
    craInitialHistoryConditionScore: makeNullable(Type.Integer()),
    craInspectionConditionScore: makeNullable(Type.Integer()),
    inspectionStandardData: Type.Any(),
    /** Survey is part of a batch */
    batchId: makeNullable(Type.String({ format: 'uuid' })),
    /** Number given bij surveyor */
    referenceNumber: makeNullable(Type.String({ maxLength: 255 })),
    /** Labels added to survey */
    labels: Type.Any(),
    /** Reference to a 3D model used in this survey */
    model3dSurveyId: makeNullable(Type.String({ format: 'uuid' })),
    /** Number given bij surveyor */
    revisionNumber: makeNullable(Type.String({ maxLength: 255 })),
    /** If survey was created by an import it will get the generated id from that import */
    createdByImportId: makeNullable(Type.String({ format: 'uuid' })),
    /** Deadline when report must be delivered to customer. Used in workflow overviews, not visible to customer. */
    reportDeadlineOnDate: makeNullable(Type.String()),
    /** Survey assigned to a user for work. Used in workflow overviews, not visible to customer. */
    assignedToUserId: makeNullable(Type.String({ format: 'uuid' })),
    statusUpdatedOn: Type.String(),
    assignmentUpdatedOn: makeNullable(Type.String()),
    linkToDocuments: makeNullable(Type.String()),
});

/** Survey performed on an object. An object can have multiple surveys */
export type Survey = Static<typeof surveyTableSchema>;
